import React from 'react';

/**
 * Pill — v2 design-system status / categorical chip.
 *
 * Tones map to the semantic + accent token families in v2-tokens.css.
 * Each tone has a soft variant (default — soft tint background, strong text)
 * and a solid variant (full color background, white text — reserved for
 * critical/active states like "kill switch ENGAGED").
 *
 * Tone usage guide (see Agentlets-Design-Principles.md §4.1, §5.1):
 *   - success : verified, healthy, approved, autonomous       (sage)
 *   - warning : pending, attention, supervised                (amber)
 *   - danger  : blocked, violation, restricted, kill active   (crimson)
 *   - brand   : critical platform attention, brand identity   (Agentlets red)
 *   - action  : interactive, link-like, "do this next"        (deep teal)
 *   - trust   : autonomy tier, trust-score band, governance   (premium violet)
 *   - cyan    : neutral data / information                    (deep cyan)
 *   - rose    : PHI / health-data classification              (deep rose)
 *   - muted   : type, archetype, secondary metadata           (neutral)
 *   - neutral : alias of muted (for call-site readability)
 *
 * Accessibility:
 *   - role="status" so screen readers announce the value as state.
 *   - When dot=true, the dot is purely decorative (aria-hidden).
 *
 * @param {Object}                                                    props
 * @param {React.ReactNode}                                           props.children
 * @param {'success'|'warning'|'danger'|'brand'|'action'|'trust'|'cyan'|'rose'|'muted'|'neutral'} [props.tone='muted']
 * @param {'soft'|'solid'}                                            [props.variant='soft']
 * @param {'sm'|'md'}                                                 [props.size='md']
 * @param {boolean}                                                   [props.dot=false]
 *   Show a small leading status dot in the same color family.
 * @param {React.ReactNode}                                           [props.icon]
 *   Optional leading icon (overrides dot).
 * @param {string}                                                    [props.className]
 * @param {string}                                                    [props.ariaLabel]
 */
export function Pill({
  children,
  tone = 'muted',
  variant = 'soft',
  size = 'md',
  dot = false,
  icon,
  className = '',
  ariaLabel,
  ...rest
}) {
  const normalizedTone = tone === 'neutral' ? 'muted' : tone;

  const sizeClass = {
    sm: 'text-[10.5px] px-2 py-px h-[18px] gap-1',
    md: 'text-[11px] px-2.5 py-0.5 h-[20px] gap-1.5',
  }[size];

  // Soft variant: tinted background + strong-color text. The token system
  // already encodes the accessible contrast pairing (e.g. success-soft +
  // success text > 4.5:1). Solid variant: full color + white text, used
  // sparingly for critical/active emphasis.
  const SOFT = {
    success: 'bg-v2-success-soft text-v2-success',
    warning: 'bg-v2-warning-soft text-v2-warning',
    danger:  'bg-v2-danger-soft text-v2-danger',
    brand:   'bg-v2-brand-soft  text-v2-brand-strong',
    action:  'bg-v2-action-soft text-v2-action-strong',
    trust:   'bg-v2-trust-soft  text-v2-trust',
    cyan:    'bg-v2-cyan-soft   text-v2-cyan',
    rose:    'bg-v2-rose-soft   text-v2-rose',
    muted:   'bg-v2-muted       text-v2-ink-secondary',
  };

  const SOLID = {
    success: 'bg-v2-success text-white',
    warning: 'bg-v2-warning text-white',
    danger:  'bg-v2-danger  text-white',
    brand:   'bg-v2-brand   text-white',
    action:  'bg-v2-action  text-white',
    trust:   'bg-v2-trust   text-white',
    cyan:    'bg-v2-cyan    text-white',
    rose:    'bg-v2-rose    text-white',
    muted:   'bg-v2-ink-secondary text-white',
  };

  const toneClass = (variant === 'solid' ? SOLID : SOFT)[normalizedTone];

  const dotSize = size === 'sm' ? 'w-1 h-1' : 'w-[5px] h-[5px]';

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      className={
        'inline-flex items-center font-semibold ' +
        'rounded-v2-pill whitespace-nowrap select-none ' +
        sizeClass + ' ' + toneClass + ' ' + className
      }
      {...rest}
    >
      {icon ? (
        <span aria-hidden="true" className="flex items-center" style={{ width: size === 'sm' ? 10 : 12, height: size === 'sm' ? 10 : 12 }}>
          {icon}
        </span>
      ) : dot ? (
        <span aria-hidden="true" className={`rounded-full bg-current ${dotSize}`} />
      ) : null}
      <span>{children}</span>
    </span>
  );
}

export default Pill;
